import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { THEMES } from '../../lib/constants';
import { cn } from '../../lib/utils';
import { 
  Settings2, 
  Palette, 
  Check,
  ArrowLeft,
  Hand,
  Pointer
} from 'lucide-react';

export function SettingsModal({ 
  isOpen, 
  onClose, 
  theme,
  onThemeChange,
  useTapMode,
  onTapModeChange
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[90vh] sm:max-h-[85vh] overflow-y-auto box-border" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Settings2 className="h-5 w-5" />
            Settings
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="controls" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="controls" className="gap-1">
              <Hand className="h-4 w-4" />
              <span className="hidden sm:inline">Controls</span>
            </TabsTrigger>
            <TabsTrigger value="theme" className="gap-1">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Theme</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Controls Tab */}
          <TabsContent value="controls" className="space-y-4 mt-4">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-2">Choose how you want to move puzzle pieces:</p>
              
              {/* Drag Mode Option */}
              <div 
                className={cn(
                  "flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all",
                  !useTapMode 
                    ? "bg-primary/10 border-primary ring-2 ring-primary/20" 
                    : "bg-card hover:bg-muted/50"
                )}
                onClick={() => onTapModeChange(false)}
              >
                <div className="space-y-1">
                  <Label className="text-base font-medium flex items-center gap-2 cursor-pointer">
                    <Hand className="h-4 w-4" />
                    Drag Mode
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Drag pieces to move them around
                  </p>
                </div>
                <Switch
                  id="drag-mode"
                  checked={!useTapMode}
                  onCheckedChange={() => onTapModeChange(false)}
                />
              </div>
              
              {/* Tap Mode Option */}
              <div 
                className={cn(
                  "flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all",
                  useTapMode 
                    ? "bg-primary/10 border-primary ring-2 ring-primary/20" 
                    : "bg-card hover:bg-muted/50"
                )}
                onClick={() => onTapModeChange(true)}
              >
                <div className="space-y-1">
                  <Label className="text-base font-medium flex items-center gap-2 cursor-pointer">
                    <Pointer className="h-4 w-4" />
                    Tap Mode
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Tap a piece to select, then tap destination to move
                  </p>
                </div>
                <Switch
                  id="tap-mode"
                  checked={useTapMode}
                  onCheckedChange={() => onTapModeChange(true)}
                />
              </div>
              
              {/* Instructions for selected mode */}
              <div className="p-4 rounded-lg border bg-muted/30 mt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
                  {useTapMode ? <Pointer className="h-4 w-4 text-primary" /> : <Hand className="h-4 w-4 text-primary" />}
                  {useTapMode ? 'How Tap Mode Works' : 'How Drag Mode Works'}
                </h4>
                {useTapMode ? (
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Tap any piece to select it (highlighted with a ring)</li>
                    <li>Tap an empty board cell to place the piece</li>
                    <li>Tap a piece on the board to swap positions</li>
                    <li>Tap the pieces tray to return a board piece</li>
                    <li>Tap the selected piece again to deselect</li>
                  </ol>
                ) : (
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Press and hold any piece to pick it up</li>
                    <li>Drag to an empty cell to place the piece</li>
                    <li>Drag onto another piece to swap positions</li>
                    <li>Drag to the pieces tray to return it</li>
                  </ol>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Theme Tab */}
          <TabsContent value="theme" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(THEMES).map(([key, config]) => (
                <Button
                  key={key}
                  variant={theme === key ? "default" : "outline"}
                  className={cn(
                    "h-auto py-4 flex flex-col gap-2",
                    theme === key && "ring-2 ring-primary ring-offset-2"
                  )}
                  onClick={() => onThemeChange(key)}
                >
                  <span className="text-2xl">{config.icon}</span>
                  <span className="text-sm font-medium">{config.name}</span>
                </Button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Save Button */}
        <div className="pt-4 border-t">
          <Button onClick={onClose} className="w-full">
            <Check className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
